<template>
  <main class="view">
    <!-- Sentinel: fixed at 75lvh, used in JS to get the exact trigger Y in current viewport coords -->
    <div class="lvh-trigger" ref="lvhTrigger"></div>

    <!-- Mobile image: always visible above the 75lvh list line, shows active row's cover -->
    <Transition name="scroll-thumb-fade">
      <div v-if="scrollThumb?.cover" class="scroll-thumb" :class="{ expanding: navigating }" @click="scrollThumb.project && navigateActive(scrollThumb)">
        <img
          :src="scrollThumb.cover.url"
          :srcset="scrollThumb.cover.srcset"
          sizes="90vw"
          :alt="scrollThumb.title"
        />
      </div>
    </Transition>

    <!-- Always-visible background cover; clicking the image navigates to the hovered/first project -->
    <div class="archive-stage">
      <img
        v-if="displayCover"
        :src="displayCover.url"
        :srcset="displayCover.srcset"
        sizes="90vw"
        @click="gotoProject"
      />
    </div>

    <div class="archive" :class="{ fading: archiveFading }" @mouseleave="hoveredRow = null">
      <div
        v-for="row in rows"
        :key="row.project || row.title"
        class="group"
        :class="{ 'thumb-active': scrollThumb?.project === row.project, 'center-active': centeredRow?.project === row.project }"
        @mouseenter="hoveredRow = row"
        @click="row.project && (centeredRow?.project === row.project ? navigateActive(row) : navigate(row))"
      >
        <div class="year" v-if="!hideYearsIndex">{{ row.year }}</div>
        <h3>
          <!-- Use plain <a> so we can set the transition cover before navigation -->
          <a
            v-if="row.project"
            :href="'/index/' + row.project"
            @click.prevent="centeredRow?.project === row.project ? navigateActive(row) : navigate(row)"
          >{{ row.title }}</a>
          <span v-else>{{ row.title }}</span>
        </h3>
      </div>
    </div>
  </main>
</template>

<script setup>
import { useStore } from '@/KQL.js'
import { setTransitionCover, clearTransitionCover } from '@/composables/useTransitionCover'
import { setBackRoute } from '@/composables/useBackRoute'
import { navFading } from '@/composables/useNavFading'
import { projectFading } from '@/composables/useProjectFading'

const kql = useStore();
const router = useRouter();
const hoveredRow = ref(null);
const archiveFading = ref(false);
const navigating = ref(false);
let navigateTimeout = null;

// keep-alive re-activation: ensure the list is visible when returning from a project
onActivated(() => {
  archiveFading.value = false
  navigating.value = false
  navFading.value = false
  projectFading.value = false
  clearTransitionCover() // drop any stale overlay from a previous navigation
  // Clear inline pixel styles locked onto the scroll-thumb before navigation
  const thumb = document.querySelector('.scroll-thumb')
  if (thumb) { thumb.style.top = ''; thumb.style.height = '' }
});

// --- Mobile scroll thumbnail ---
const centeredRow = ref(null)
const lvhTrigger = ref(null)

const updateCenteredRow = () => {
  // Query live DOM — avoids any ref-array clearing race with Vue's update cycle
  const groups = document.querySelectorAll('.archive .group')
  if (!groups.length) return
  // Use sentinel element to get the exact 75lvh position in current viewport coords,
  // which stays stable as Safari's dynamic toolbar shows/hides
  const triggerY = lvhTrigger.value
    ? lvhTrigger.value.getBoundingClientRect().top
    : window.innerHeight * 0.75
  let closest = null
  let closestDist = Infinity
  groups.forEach((el, i) => {
    const rect = el.getBoundingClientRect()
    const elCenter = (rect.top + rect.bottom) / 2
    const dist = Math.abs(elCenter - triggerY)
    if (dist < closestDist) {
      closestDist = dist
      closest = rows.value[i] ?? null
    }
  })
  if (closest) centeredRow.value = closest
}

const onScroll = () => updateCenteredRow()

// Always show the active row's cover on mobile (CSS hides it on desktop)
const scrollThumb = computed(() => centeredRow.value)

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  nextTick(updateCenteredRow)
})

onActivated(() => nextTick(updateCenteredRow))

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  clearTimeout(navigateTimeout)
})
// --- End mobile scroll thumbnail ---

const hideYearsIndex = computed(() => kql.site?.content?.hideyearsindex === 'true')

const rows = computed(() =>
  Object.values(kql.projects || {}).map(c => ({
    year: String(c.content?.year || ''),
    title: c.title,
    project: c.uid,
    cover: c.cover?.url ? c.cover : null,
  }))
);

// Preload all cover images as soon as the list is available
watch(rows, (newRows) => {
  newRows.forEach(row => {
    if (row.cover?.url) {
      const img = new Image()
      img.src = row.cover.url
    }
  })
}, { immediate: true })

// Only show a cover when a row is actively hovered — no default/first cover
const displayCover = computed(() => hoveredRow.value?.cover || null);

// Fade the list, then set the overlay and navigate.
// The 150ms gap lets the archive-stage image stay visible while the text disappears;
// the overlay picks up the image for the remainder of the project load.
const navigate = (row) => {
  archiveFading.value = true
  navFading.value = true
  navigateTimeout = setTimeout(() => {
    setBackRoute('/index')
    if (row.cover) setTransitionCover(row.cover)
    router.push('/index/' + row.project)
  }, 600) // matches archive fade duration
}

// Mobile active-row tap: expand the cover image to fill the viewport, then navigate
const navigateActive = (row) => {
  if (navigating.value) return
  navigating.value = true
  archiveFading.value = true
  navFading.value = true
  navigateTimeout = setTimeout(() => {
    // Lock the scroll-thumb at its current pixel position before router.push,
    // which triggers Safari to show the URL bar and shrink dvh — without this
    // the element snaps the moment the viewport changes.
    const thumb = document.querySelector('.scroll-thumb')
    if (thumb) {
      const rect = thumb.getBoundingClientRect()
      thumb.style.top = `${rect.top}px`
      thumb.style.height = `${rect.height}px`
    }
    setBackRoute('/index')
    if (row.cover) setTransitionCover(row.cover)
    router.push('/index/' + row.project)
  }, 1400) // 800ms expand + 400ms pause before navigate
}

const gotoProject = () => {
  const row = hoveredRow.value || rows.value[0]
  if (row) navigate(row)
}
</script>
